import {
  CreateCampaignRequest,
  UpdateCampaignRequest,
  SaveEmailCampaignConfigsRequest,
  SendTestCampaignRequest,
  GetPatronListCountRequest,
  EmailCampaignData,
  EmailCampaignOpenRate,
  TargetGroup,
  MerchantEmailCampaignSettings,
  MerchantEmail,
  CreateCampaignResponse
} from "../types/EmailCampaignTypes";

export class EmailCampaignService {
  private baseUrl: string;

  constructor() {
    // TODO: Get the actual API URL from environment variables or config
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || "";
  }

  /**
   * Convert local date and time to UTC ISO 8601 string
   * @param date - Local date string (YYYY-MM-DD)
   * @param time - Local time string (HH:mm)
   * @returns UTC datetime string in ISO 8601 format
   */
  private convertToUTCDateTime(date: string, time: string): string {
    // Create a Date object from the local date and time
    const localDateTime = new Date(`${date}T${time}:00`);
    
    // Convert to UTC ISO 8601 string
    return localDateTime.toISOString();
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}/emailmgmt/${endpoint}`;

    const defaultHeaders = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("businessAccessToken")}`
    };

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers
      }
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      // Handle the serverless response format
      if (data.statusCode && data.statusCode !== 200) {
        throw new Error(
          data.body ? JSON.parse(data.body).message : "API Error"
        );
      }

      return data.body ? JSON.parse(data.body) : data;
    } catch (error) {
      console.error(`API Error for ${endpoint}:`, error);
      throw error;
    }
  }

  // Get all campaigns (both outgoing and previous)
  async listCampaigns(businessId: string): Promise<EmailCampaignData[]> {
    const [outgoing, previous] = await Promise.all([
      this.getOutgoingCampaigns(businessId),
      this.getPreviousCampaigns(businessId)
    ]);
    return [...outgoing, ...previous];
  }

  // Create a new campaign
  async createCampaign(
    request: CreateCampaignRequest,
    sendOutDate?: string,
    sendOutTime?: string
  ): Promise<CreateCampaignResponse> {
    // Convert separate date/time to UTC datetime if provided
    const requestPayload = { ...request };
    if (request.SendType === 2 && sendOutDate && sendOutTime) {
      requestPayload.SendOutDateTime = this.convertToUTCDateTime(sendOutDate, sendOutTime);
    }
    
    return this.makeRequest<CreateCampaignResponse>(
      `campaigns/business/${request.BusinessId}`,
      {
        method: "POST",
        body: JSON.stringify(requestPayload)
      }
    );
  }

  // Get outgoing/scheduled campaigns
  async getOutgoingCampaigns(businessId: string): Promise<EmailCampaignData[]> {
    return this.makeRequest<EmailCampaignData[]>(
      `campaigns/business/${businessId}/outgoing`
    );
  }

  // Get previous/sent campaigns
  async getPreviousCampaigns(businessId: string): Promise<EmailCampaignData[]> {
    return this.makeRequest<EmailCampaignData[]>(
      `campaigns/business/${businessId}/previous`
    );
  }

  // Removed getEmailCampaignData as unused by current frontend flows

  // Get email campaign open rate
  async getEmailCampaignOpenRate(
    campaignId: string
  ): Promise<EmailCampaignOpenRate> {
    return this.makeRequest<EmailCampaignOpenRate>(
      `campaigns/${campaignId}/openrate`
    );
  }

  // Get patron list count by group
  async getPatronListCount(
    request: GetPatronListCountRequest
  ): Promise<{ Count: number }> {
    return this.makeRequest<{ Count: number }>(
      `campaigns/business/${request.BusinessId}/patrons/count`,
      {
        method: "POST",
        body: JSON.stringify({
          GroupId: request.GroupId,
          BusinessId: request.BusinessId
        })
      }
    );
  }

  // Get total number of recipients
  async getTotalNoOfRecipients(businessId: string): Promise<{ Count: number }> {
    return this.makeRequest<{ Count: number }>(
      `campaigns/business/${businessId}/recipients/total`
    );
  }

  // Get email campaign target groups
  async getEmailCampaignTargetGroups(
    businessId: string
  ): Promise<TargetGroup[]> {
    return this.makeRequest<TargetGroup[]>(
      `campaigns/business/${businessId}/targetgroups`
    );
  }

  // Load merchant email campaign settings
  async loadMerchantEmailCampaignSettings(
    businessId: string
  ): Promise<MerchantEmailCampaignSettings> {
    return this.makeRequest<MerchantEmailCampaignSettings>(
      `campaigns/business/${businessId}/settings`
    );
  }

  // Get merchant email by campaign ID
  async getMerchantEmailByCampaignId(
    campaignId: string
  ): Promise<MerchantEmail> {
    return this.makeRequest<MerchantEmail>(`campaigns/${campaignId}`);
  }

  // Update an existing campaign
  async updateCampaign(
    request: UpdateCampaignRequest,
    sendOutDate?: string,
    sendOutTime?: string
  ): Promise<CreateCampaignResponse> {
    // Convert separate date/time to UTC datetime if provided
    const requestPayload = { ...request };
    if (request.SendType === 2 && sendOutDate && sendOutTime) {
      requestPayload.SendOutDateTime = this.convertToUTCDateTime(sendOutDate, sendOutTime);
    }
    
    return this.makeRequest<CreateCampaignResponse>(
      `campaigns/business/${request.BusinessId}/update`,
      {
        method: "PUT",
        body: JSON.stringify(requestPayload)
      }
    );
  }

  // Save email campaign configs/settings
  async saveEmailCampaignConfigs(
    request: SaveEmailCampaignConfigsRequest
  ): Promise<{ Success: boolean }> {
    return this.makeRequest<{ Success: boolean }>(
      `campaigns/business/${request.BusinessId}/settings/save`,
      {
        method: "POST",
        body: JSON.stringify(request)
      }
    );
  }

  // Send test campaign
  async sendTestCampaign(
    request: SendTestCampaignRequest
  ): Promise<{ Success: boolean }> {
    return this.makeRequest<{ Success: boolean }>(
      `campaigns/business/${request.BusinessId}/test`,
      {
        method: "POST",
        body: JSON.stringify(request)
      }
    );
  }

  // Removed uploadLogo as current UI uploads directly to S3
}

export default EmailCampaignService;
